import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button } from '@mui/material';
import { ArrowLeft, Clock } from 'lucide-react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';
import { dummyProfilingData } from './dummyProfilingData';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import heartLoading from "../../../../assests/heartLoading.gif"
import preloader from "../../../../assests/preloader1.gif"
import Plot from 'react-plotly.js';
import LoadingScreen from './LoadingScreen';



// A simple component for the horizontal bar chart
const DataBarChart = ({ data }) => {
    if (!Array.isArray(data) || data.length === 0) {
        return <Typography variant="caption" color="text.secondary">No data</Typography>;
    }
    const maxValue = Math.max(...data.map(d => d.value));
    return (
        <Box sx={{ width: '100%', pr: 2 }}>
            {data.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ width: '80px', textAlign: 'right', mr: 1 }}>{item.label}</Typography>
                    <Box sx={{ flexGrow: 1, bgcolor: '#e0e0e0', borderRadius: '4px' }}>
                        <Box sx={{
                            width: `${maxValue > 0 ? (item.value / maxValue) * 100 : 0}%`,
                            bgcolor: '#1976d2',
                            height: '16px',
                            borderRadius: '4px',
                            transition: 'width 0.5s ease-in-out'
                        }} />
                    </Box>
                </Box>
            ))}
        </Box>
    );
};



const DataProfiling = ({ onPrevious, nextStep, id }) => {
    const [loading, setLoading] = useState(true);
    const [resultsLoading, setResultsLoading] = useState(false);
    const [profilingResults, setProfilingResults] = useState([]);
    const [businessTerms, setBusinessTerms] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [selectedResult, setSelectedResult] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [popupAssetName, setPopupAssetName] = useState("")
    const [profilingResultStatus, setProfilingResultStatus] = useState("")
    const [currentRowCount,  setCurrentRowCount] = useState("")
    const [graphOpen, setGraphOpen] = useState(false);
    const [graphColumn, setGraphColumn] = useState(null);
    const [error, setError] = useState("");
    const [profilingJobStatus, setProfilingJobStatus] = useState([]);
    const [totalFiles, setTotalFiles] = useState(0);
    const [completedFiles, setCompletedFiles] = useState(0);
    const [finalResult, setFinalResult] = useState(false);
    const [isReRunning, setIsReRunning] = useState(false);
    const [profilingViewData, setProfilingViewData] = useState([]);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [runningAssets, setRunningAssets] = useState(new Set());
    const isFetchingResults = useRef(false);
    console.log(
        "current row count", currentRowCount
    )
    // console.log("selected results", selectedResult)

    // console.log("profiling Results", profilingResults)

    // --- Loading Progress State ---
    const [progress, setProgress] = useState(0);
    const [currentPhase, setCurrentPhase] = useState('Initializing');
    const totalExpectedTime = 15 * 60; // 15 minutes in seconds
    const initialPhaseTime = 20; // seconds for first 3%
    const phases = [
        { name: 'Initializing', maxProgress: 5 },
        { name: 'Scanning data sources', maxProgress: 15 },
        { name: 'Analyzing data structure', maxProgress: 35 },
        { name: 'Profiling data quality', maxProgress: 55 },
        { name: 'Generating insights', maxProgress: 75 },
        { name: 'Finalizing analysis', maxProgress: 85 }
    ];
    useEffect(() => {
        if (!loading) return;
        let start = Date.now();
        let animation;
        function animate() {
            const elapsed = (Date.now() - start) / 1000; // seconds
            let pct;
            if (elapsed < initialPhaseTime) {
                pct = (elapsed / initialPhaseTime) * 3; // 0-3% in first 20s
            } else {
                const remainingTime = totalExpectedTime - initialPhaseTime;
                const elapsedAfterInitial = elapsed - initialPhaseTime;
                pct = 3 + ((elapsedAfterInitial / remainingTime) * (85 - 3));
            }
            pct = Math.min(pct, 85);
            setProgress(pct);
            // Set phase
            const phase = phases.find(ph => pct <= ph.maxProgress) || phases[phases.length - 1];
            setCurrentPhase(phase.name);
            if (pct < 85) {
                animation = requestAnimationFrame(animate);
            }
        }
        animation = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animation);
    }, [loading]);
    // Calculate remaining time
    const remainingSeconds = Math.max(0, Math.round(((85 - progress) / 85) * totalExpectedTime));
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    // --- END Loading Progress State ---

    const fetchProfilingView = async (showLoading = true) => {
        try {
            // Only show loading on first call
            if (showLoading && isFirstLoad) {
                setLoading(true);
            }
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/get-profiling-view/${id}`);
            if (response?.status === 200 || response?.status === 201) {
                setProfilingViewData(response?.data?.profiling_job_status || []);
                setFinalResult(true);
                if (isFirstLoad) {
                    setIsFirstLoad(false);
                }
            }
        } catch (err) {
            setError("Failed to load profiling view data.");
        } finally {
            if (showLoading && isFirstLoad) {
                setLoading(false);
            }
        }
    };

    const fetchResults = async (dataAssetId) => {
        // Prevent duplicate calls
        if (isFetchingResults.current) {
            return;
        }
        
        isFetchingResults.current = true;
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/asset_profiling/${dataAssetId}`);
            if (response?.status === 200 || response?.status === 201) {
                setProfilingResults(response?.data?.profiling_results);
            }
        } catch (err) {
            setError("Failed to load profiling results.");
        } finally {
            isFetchingResults.current = false;
        }
    };

    const handleBusinessTermChange = (assetName, columnName, value) => {
        setBusinessTerms(prev => ({
            ...prev,
            [`${assetName}-${columnName}`]: value
        }));
    };

    const areAllBusinessTermsFilled = () => {
        return Object.values(businessTerms).every(term => term.trim() !== '');
    };

    const handleSaveDraft = async () => {
        setIsSaving(true);
        try {
            const payload = profilingResults.map(asset => ({
                assetName: asset.assetName,
                columns: asset.columns.map(col => ({
                    columnName: col.columnName,
                    dataType: col.dataType,
                    businessTerm: businessTerms[`${asset.assetName}-${col.columnName}`] || '',
                    nullPercentage: col.nullPercentage,
                    uniquePercentage: col.uniquePercentage,
                    distribution: col.dataDistribution
                }))
            }));
            await axios.post(`${process.env.REACT_APP_API_URL}/save-profiling-draft`, payload);
            toast.success('Draft saved successfully!');
        } catch (error) {
            console.error('Error saving draft:', error);
            toast.error('Failed to save draft. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleReadyForDQ = async () => {
        if (!areAllBusinessTermsFilled()) {
            toast.error('Please fill in all business terms before proceeding.');
            return;
        }
        // Build payload
        nextStep();
        const payload = {ProducId : id}

        // try {

            
        // const response = await axios.post(`${process.env.REACT_APP_API_URL}/dataQuality/start/${id}`);
        // if(response.status === 200 || 201){
        //         toast.success("Rule initialization successful");
        // nextStep();
        // }
        // } catch (error) {
        //     console.error('DQ Checks API failed (expected):', error);
        //     toast.error('DQ Checks API failed (but proceeding)...');
        // }
    };

    const handleRunAgain = async (dataAssetId) => {
        // Add this asset to the running set
        setRunningAssets(prev => new Set(prev).add(dataAssetId));
        setError("");
        try {
            // Trigger profiling API for specific asset
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/trigger_profiling-asset/${dataAssetId}`);
            if (response.status === 200 || response.status === 201) {
                toast.success("Profiling restarted successfully!");
                // Refresh the profiling view data without showing loading
                setTimeout(() => {
                    // Remove this asset from the running set
                    setRunningAssets(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(dataAssetId);
                        return newSet;
                    });
                    fetchProfilingView(false);
                }, 2000);
            }
        } catch (error) {
            console.error('Failed to restart profiling:', error);
            toast.error('Failed to restart profiling. Please try again.');
            // Remove this asset from the running set on error
            setRunningAssets(prev => {
                const newSet = new Set(prev);
                newSet.delete(dataAssetId);
                return newSet;
            });
        }
    };

    const handleViewResult = async (dataAssetId, assetName) => {
        setResultsLoading(true);
        setPopupAssetName(assetName);
        try {
            await fetchResults(dataAssetId);
            setModalOpen(true);
        } catch (err) {
            toast.error('Failed to load profiling results.');
        } finally {
            setResultsLoading(false);
        }
    };

    useEffect(() => {
        // Initial fetch
        fetchProfilingView(true);
        
        // Set up polling every 1 minute (60000ms)
        const pollInterval = setInterval(() => {
            fetchProfilingView(false); // Don't show loading on subsequent calls
        }, 60000);
        
        // Cleanup on unmount
        return () => clearInterval(pollInterval);
    }, [id]);

    if (loading) {
        return (
            <div className="fixed inset-0 bg-white flex items-center justify-center z-50 w-full h-full">
            <div className="flex flex-col items-center space-y-3">
              <img src={preloader} alt="Loading..." width={200} />
              <span className="text-gray-700 text-[20px]">
              Hang tight! The resources are loading...
              </span>
            </div>
          </div>
        );
    }
    if (resultsLoading) {
        return (
            <div className="fixed inset-0 bg-white flex items-center justify-center z-50 w-full h-full">
            <div className="flex flex-col items-center space-y-3">
              <img src={preloader} alt="Loading..." width={200} />
              <span className="text-gray-700 text-[20px]">
              Hang tight! The resources are loading...
              </span>
            </div>
          </div>
        );
    }

    return (
        <Box sx={{ maxWidth: '90%', margin: 'auto', mt: 4 }} className="rounded pb-4">
            {finalResult && (
                <>
                    <Box sx={{ mb: 4, p: 3 }}>
                        <Typography 
                            variant="h4" 
                            component="h1" 
                            sx={{
                                color: '#0000a0',
                                fontWeight: 700,
                                fontSize: '1.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            <Box
                                sx={{
                                    width: '4px',
                                    height: '24px',
                                    backgroundColor: '#0000a0',
                                    borderRadius: '2px'
                                }}
                            />
                            Profiling Results
                        </Typography>
                    </Box>
                    
                    <div className='w-[90%] m-auto mt-6'>
                        <TableContainer 
                            component={Paper} 
                            sx={{ 
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', 
                                border: '1px solid #e0e0e0', 
                                borderRadius: '12px',
                                maxHeight: '500px',
                                overflow: 'auto'
                            }}
                        >
                            <Table sx={{ minWidth: 650 }} stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ 
                                            color: "white", 
                                            fontWeight: 600, 
                                            fontSize: '14px',
                                            padding: '16px 20px',
                                            borderBottom: '2px solid #000080',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 2,
                                            backgroundColor: '#0000a0 !important'
                                        }}>
                                            Data Asset Name
                                        </TableCell>
                                        <TableCell sx={{ 
                                            color: "white", 
                                            fontWeight: 600, 
                                            fontSize: '14px',
                                            padding: '16px 20px',
                                            borderBottom: '2px solid #000080',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 2,
                                            backgroundColor: '#0000a0 !important'
                                        }}>
                                            Run Status
                                        </TableCell>
                                        <TableCell sx={{ 
                                            color: "white", 
                                            fontWeight: 600, 
                                            fontSize: '14px',
                                            padding: '16px 20px',
                                            borderBottom: '2px solid #000080',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 2,
                                            backgroundColor: '#0000a0 !important'
                                        }}>
                                            View Result
                                        </TableCell>
                                        <TableCell sx={{ 
                                            color: "white", 
                                            fontWeight: 600, 
                                            fontSize: '14px',
                                            padding: '16px 20px',
                                            borderBottom: '2px solid #000080',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 2,
                                            backgroundColor: '#0000a0 !important'
                                        }}>
                                            Run Again
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {profilingViewData.map((row, index) => (
                                        <TableRow 
                                            key={row.data_asset_id}
                                            sx={{ 
                                                backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                                                '&:hover': {
                                                    backgroundColor: '#f0f8ff',
                                                    transition: 'background-color 0.2s ease'
                                                }
                                            }}
                                        >
                                            <TableCell sx={{ 
                                                padding: '16px 20px',
                                                fontWeight: 600,
                                                fontSize: '14px',
                                                color: '#2c3e50'
                                            }}>
                                                {row.data_asset_name}
                                            </TableCell>
                                            <TableCell sx={{ 
                                                padding: '16px 20px',
                                                fontSize: '14px'
                                            }}>
                                                <Box
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        px: 2,
                                                        py: 0.5,
                                                        borderRadius: '20px',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        textTransform: 'uppercase',
                                                        backgroundColor: row.status === "Completed" ? '#e8f5e8' : 
                                                                        row.status === "Failed" ? '#ffeaea' : 
                                                                        row.status === "In Progress" ? '#fff3e0' : '#f3f4f6',
                                                        color: row.status === "Completed" ? '#2e7d32' : 
                                                               row.status === "Failed" ? '#d32f2f' : 
                                                               row.status === "In Progress" ? '#f57c00' : '#6b7280'
                                                    }}
                                                >
                                                    {row.status}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ 
                                                padding: '16px 20px',
                                                fontSize: '14px'
                                            }}>
                                                <Button 
                                                    variant="text" 
                                                    sx={{ 
                                                        color: '#0000a0', 
                                                        fontWeight: 600,
                                                        '&:hover': {
                                                            backgroundColor: '#f0f8ff'
                                                        },
                                                        '&:disabled': {
                                                            color: '#9ca3af',
                                                            backgroundColor: 'transparent'
                                                        }
                                                    }} 
                                                    onClick={() => handleViewResult(row.data_asset_id, row.data_asset_name)}
                                                    disabled={row.status !== "Completed"}
                                                >
                                                    View Result
                                                </Button>
                                            </TableCell>
                                            <TableCell sx={{ 
                                                padding: '16px 20px',
                                                fontSize: '14px'
                                            }}>
                                                <Button 
                                                    variant="text" 
                                                    sx={{ 
                                                        color: '#0000a0', 
                                                        fontWeight: 600,
                                                        '&:hover': {
                                                            backgroundColor: '#f0f8ff'
                                                        },
                                                        '&:disabled': {
                                                            color: '#9ca3af',
                                                            backgroundColor: 'transparent'
                                                        }
                                                    }} 
                                                    onClick={() => handleRunAgain(row.data_asset_id)}
                                                    disabled={row.status === "In Progress" || row.status === "Pending" || runningAssets.has(row.data_asset_id)}
                                                >
                                                    {runningAssets.has(row.data_asset_id) ? 'Running...' : 'Run Again'}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>
                    
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2, mr: 4 }}>
                        <Button 
                            variant="contained" 
                            sx={{backgroundColor: '#0000a0'}}
                            onClick={handleReadyForDQ}
                        >
                            Ready to Run DQ Checks
                        </Button>
                    </Box>
                </>
            )}
            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="lg" fullWidth>
                <div className='flex justify-between items-center '>
                <DialogTitle sx={{ fontWeight: 700, fontSize: 20 }}>{popupAssetName}</DialogTitle>
                
                <IconButton sx={{marginRight: '20px'}} onClick={() => setModalOpen(false) } >
                <CloseIcon />
                </IconButton>
                </div>
                <DialogContent>
                    {/* <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>{selectedResult?.assetPath || selectedResult?.assetFullPath || selectedResult?.assetTable || ''}</Typography> */}
                    <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                borderRadius: '16px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                                padding: 3,
                                width: '200px',
                                height: '120px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                border: '1px solid #d3d3d3',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                '&:hover': {
                                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.2) !important',
                                    transform: 'translateY(-6px) !important',
                                    border: '1px solid #0000a0 !important',
                                    backgroundColor: '#f8f9ff'
                                }
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, color: '#0000a0', fontSize: '1.1rem' }}
                                >
                                    Columns Analyzed
                                </Typography>
                                <Box
                                    sx={{
                                        height: '3px',
                                        width: '40px',
                                        backgroundColor: '#0000a0',
                                        borderRadius: '2px',
                                        mt: 0.5,
                                    }}
                                />
                            </Box>
                            <Typography
                                variant="h4"
                                sx={{ fontWeight: 700, color: '#3C3C3C', fontSize: '2rem' }}
                            >
                                {profilingResults.length > 0 ? profilingResults[0]?.columns?.length || 0 : 0}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                borderRadius: '16px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                                padding: 3,
                                width: '200px',
                                height: '120px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                border: '1px solid #d3d3d3',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                '&:hover': {
                                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.2) !important',
                                    transform: 'translateY(-6px) !important',
                                    border: '1px solid #0000a0 !important',
                                    backgroundColor: '#f8f9ff'
                                }
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, color: '#0000a0', fontSize: '1.1rem' }}
                                >
                                    Rows Profiled
                                </Typography>
                                <Box
                                    sx={{
                                        height: '3px',
                                        width: '40px',
                                        backgroundColor: '#0000a0',
                                        borderRadius: '2px',
                                        mt: 0.5,
                                    }}
                                />
                            </Box>
                            <Typography
                                variant="h4"
                                sx={{ fontWeight: 700, color: '#3C3C3C', fontSize: '2rem' }}
                            >
                                {profilingResults.length > 0 ? profilingResults[0]?.columns?.[0]?.count || 'N/A' : 'N/A'}
                            </Typography>
                        </Box>
                    </Box>
                    <TableContainer 
                        component={Paper} 
                        sx={{ 
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', 
                            border: '1px solid #e0e0e0', 
                            borderRadius: '12px',
                            marginTop: "20px",
                            maxHeight: '500px',
                            overflow: 'auto'
                        }}
                    >
                        <Table sx={{ minWidth: 650 }} stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ 
                                        color: "white", 
                                        fontWeight: 600, 
                                        fontSize: '14px',
                                        padding: '16px 20px',
                                        borderBottom: '2px solid #000080',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                        backgroundColor: '#0000a0 !important'
                                    }}>
                                        Column
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: "white", 
                                        fontWeight: 600, 
                                        fontSize: '14px',
                                        padding: '16px 20px',
                                        borderBottom: '2px solid #000080',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                        backgroundColor: '#0000a0 !important'
                                    }}>
                                        Data Type
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: "white", 
                                        fontWeight: 600, 
                                        fontSize: '14px',
                                        padding: '16px 20px',
                                        borderBottom: '2px solid #000080',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                        backgroundColor: '#0000a0 !important'
                                    }}>
                                        CDE
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: "white", 
                                        fontWeight: 600, 
                                        fontSize: '14px',
                                        padding: '16px 20px',
                                        borderBottom: '2px solid #000080',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                        backgroundColor: '#0000a0 !important'
                                    }}>
                                        Business Term
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: "white", 
                                        fontWeight: 600, 
                                        fontSize: '14px',
                                        padding: '16px 20px',
                                        borderBottom: '2px solid #000080',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                        backgroundColor: '#0000a0 !important'
                                    }}>
                                        Null %
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: "white", 
                                        fontWeight: 600, 
                                        fontSize: '14px',
                                        padding: '16px 20px',
                                        borderBottom: '2px solid #000080',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                        backgroundColor: '#0000a0 !important'
                                    }}>
                                        Statistics
                                    </TableCell>
                                    <TableCell sx={{ 
                                        color: "white", 
                                        fontWeight: 600, 
                                        fontSize: '14px',
                                        padding: '16px 20px',
                                        borderBottom: '2px solid #000080',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                        backgroundColor: '#0000a0 !important'
                                    }}>
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {profilingResults.length > 0 && profilingResults[0]?.columns?.map((col, index) => (
                                    <TableRow 
                                        key={col.column_name}
                                        sx={{ 
                                            backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                                            '&:hover': {
                                                backgroundColor: '#f0f8ff',
                                                transition: 'background-color 0.2s ease'
                                            }
                                        }}
                                    >
                                        <TableCell sx={{ 
                                            padding: '16px 20px',
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            color: '#2c3e50'
                                        }}>
                                            {col.column_name}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            padding: '16px 20px',
                                            fontSize: '14px',
                                            color: '#555'
                                        }}>
                                            {col.type}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            padding: '16px 20px',
                                            fontSize: '14px'
                                        }}>
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    px: 2,
                                                    py: 0.5,
                                                    borderRadius: '20px',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    backgroundColor: col.isCde ? '#e8f5e8' : '#fff3e0',
                                                    color: col.isCde ? '#2e7d32' : '#f57c00'
                                                }}
                                            >
                                                {col.isCde ? "Yes" : "No"}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ 
                                            padding: '16px 20px',
                                            fontSize: '14px',
                                            color: '#555'
                                        }}>
                                            {col.businessterm === null ? "N/A" : col.businessterm}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            padding: '16px 20px',
                                            fontSize: '14px',
                                            color: '#555'
                                        }}>
                                            {col.nullPercentage === null ? "N/A" : `${col.nullPercentage || 0}%`}
                                        </TableCell>
                                        <TableCell sx={{ 
                                            padding: '16px 20px',
                                            fontSize: '14px',
                                            color: '#555'
                                        }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                <span><strong>Mean:</strong> {col.mean === null ? "N/A" : col.mean}</span>
                                                <span><strong>Max:</strong> {col.max === null ? "N/A" : col.max}</span>
                                                <span><strong>Min:</strong> {col.min === null ? "N/A" : col.min}</span>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ 
                                            padding: '16px 20px',
                                            fontSize: '14px'
                                        }}>
                                            <Button 
                                                variant="text" 
                                                size="small" 
                                                sx={{ 
                                                    color: '#0000a0', 
                                                    fontWeight: 600,
                                                    '&:hover': {
                                                        backgroundColor: '#f0f8ff'
                                                    }
                                                }} 
                                                onClick={() => { setGraphOpen(true); setGraphColumn(col); }}
                                            >
                                                View Plot
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
            </Dialog>
            {/* Plotly Graph Popup */}
            <Dialog open={graphOpen} onClose={() => setGraphOpen(false)} maxWidth="md" fullWidth>
                <div className='flex justify-end items-center p-4'>
                    <IconButton onClick={() => setGraphOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </div>
                <DialogContent sx={{ p: 0 }}>
                    <Plot
                        data={graphColumn?.chart?.data || []}
                        layout={{
                            ...(graphColumn?.chart?.layout || {}),
                            margin: { t: 60, b: 80, ...(graphColumn?.chart?.layout?.margin || {}) },
                            autosize: true,
                            width: null,
                            height: 500
                        }}
                        style={{ width: '100%', height: '100%' }}
                        useResizeHandler={true}
                    />
                </DialogContent>
            </Dialog>
            <Toaster position="top-right" />
        </Box>
    );
};

export default DataProfiling;